import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";

/**
 * "Read while you wait" summary for the phishing-office start screen. Three short
 * slides (what/who and why a human is the control, the three-step attack chain,
 * key definitions) shown in the reader while the 3D room downloads. Content only;
 * the reader UI lives in ui/StartReader.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. urgent email with a lookalike sender and a hidden link target    */
/* ------------------------------------------------------------------ */
function UrgentEmailArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label="Example: urgent email" aria="A bank security email with a lookalike sender address, an urgent deadline and a button whose link goes to a different address" height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        Bank Security
      </text>
      <text x="344" y="53" fontSize="11" fill={C.muted} textAnchor="end">
        9:12 AM
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <text x="16" y="91" fontSize="10.5" fill={C.muted}>
        From
      </text>
      <text x="52" y="91" fontSize="11.5" fontFamily={MONO} fill={C.soft}>
        alerts@you<tspan fill={C.red} fontWeight="700">br</tspan>ank.com
      </text>
      <rect className="sr-anim-ring" x="118" y="79" width="19" height="16" rx="4" fill="none" stroke={C.red} strokeWidth="1.5" />
      <rect x="118.5" y="79.5" width="18" height="15" rx="4" fill="none" stroke={C.red} strokeOpacity="0.7" />

      <text x="16" y="118" fontSize="12.5" fontWeight="700" fill={C.text}>
        Unusual activity on your account
      </text>
      <rect x="16" y="130" width="300" height="6" rx="3" fill={C.line} />
      <rect x="16" y="143" width="326" height="6" rx="3" fill={C.line} />
      <rect x="16" y="156" width="214" height="6" rx="3" fill={C.line} />

      <rect x="16" y="174" width="160" height="28" rx="8" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.45" />
      <text x="96" y="192" fontSize="12" fontWeight="700" fill={C.mint} textAnchor="middle">
        Review your account
      </text>
      <rect x="16.5" y="210.5" width="327" height="26" rx="6" fill={C.code} stroke={C.line} />
      <text x="28" y="227" fontSize="10.5" fill={C.muted}>
        Link goes to
      </text>
      <text x="96" y="227" fontSize="11" fontFamily={MONO} fill={C.red}>
        youbrank.com/login
      </text>

      <rect x="16.5" y="248.5" width="327" height="32" rx="8" fill={C.amber} fillOpacity="0.1" stroke={C.amber} strokeOpacity="0.4" />
      <circle className="sr-anim-ring" cx="33" cy="264.5" r="5.5" fill="none" stroke={C.amber} strokeWidth="1.5" />
      <circle cx="33" cy="264.5" r="1.8" fill={C.amber} />
      <text x="46" y="268.5" fontSize="12" fontWeight="600" fill={C.amber}>
        Verify within 24 hours or lose access
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. the three-step chain: fake login, fake panic, live call          */
/* ------------------------------------------------------------------ */
function StepNum({ n, y, active }: { n: number; y: number; active?: boolean }): JSX.Element {
  return (
    <g>
      {active ? <circle className="sr-anim-ring" cx="34" cy={y} r="11" fill="none" stroke={C.red} strokeWidth="1.5" /> : null}
      <circle cx="34" cy={y} r="11" fill={active ? C.red : C.mint} fillOpacity="0.14" stroke={active ? C.red : C.mint} strokeOpacity="0.5" />
      <text x="34" y={y + 4.5} fontSize="12.5" fontWeight="700" fill={active ? C.red : C.mint} textAnchor="middle">
        {n}
      </text>
    </g>
  );
}

function Connector({ y }: { y: number }): JSX.Element {
  return (
    <g stroke={C.muted} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none">
      <line x1="34" y1={y} x2="34" y2={y + 8} />
      <polyline points={`30.5,${y + 5} 34,${y + 9} 37.5,${y + 5}`} />
    </g>
  );
}

function ChainArt(): JSX.Element {
  const H = 300;
  const digit = (i: number, d: string) => (
    <g key={i}>
      <rect x={256 + i * 22} y="212" width="18" height="24" rx="4" fill={C.code} stroke={C.red} strokeOpacity="0.55" />
      <text x={265 + i * 22} y="229" fontSize="13" fontWeight="700" fontFamily={MONO} fill={C.red} textAnchor="middle">
        {d}
      </text>
    </g>
  );
  return (
    <Frame label="Example: one email, three steps" aria="Three steps of one attack: a fake login page, a fake empty balance asking for a phone number, and a live call asking for a one-time code" height={H}>
      {/* step 1 */}
      <rect x="16.5" y="34.5" width="327" height="64" rx="10" fill={C.panel2} stroke={C.line} />
      <StepNum n={1} y={66} />
      <text x="56" y="58" fontSize="12.5" fontWeight="700" fill={C.text}>
        Fake login page
      </text>
      <text x="56" y="75" fontSize="10.5" fill={C.muted}>
        A lookalike address asks for your password
      </text>
      <rect x="56" y="82" width="104" height="11" rx="3" fill={C.code} />
      <text x="62" y="90.5" fontSize="9" fontFamily={MONO} fill={C.red}>
        youbrank.com/login
      </text>
      <Connector y={100} />

      {/* step 2 */}
      <rect x="16.5" y="112.5" width="327" height="64" rx="10" fill={C.panel2} stroke={C.line} />
      <StepNum n={2} y={144} />
      <text x="56" y="136" fontSize="12.5" fontWeight="700" fill={C.text}>
        Fake panic
      </text>
      <text x="56" y="153" fontSize="10.5" fill={C.muted}>
        Balance shows zero; asks for your phone number
      </text>
      <rect className="sr-anim-pulse" x="56" y="159" width="64" height="12" rx="6" fill={C.red} fillOpacity="0.14" stroke={C.red} strokeOpacity="0.45" style={{ transformOrigin: "88px 165px" }} />
      <text x="88" y="168" fontSize="9" fontWeight="700" fontFamily={MONO} fill={C.red} textAnchor="middle">
        $0.00
      </text>
      <Connector y={178} />

      {/* step 3 */}
      <rect x="16.5" y="190.5" width="327" height="64" rx="10" fill={C.red} fillOpacity="0.07" stroke={C.red} strokeOpacity="0.45" />
      <StepNum n={3} y={222} active />
      <text x="56" y="214" fontSize="12.5" fontWeight="700" fill={C.text}>
        Live call
      </text>
      <text x="56" y="231" fontSize="10.5" fill={C.muted}>
        A caller asks you to read out your code
      </text>
      <text x="56" y="245" fontSize="10.5" fill={C.muted}>
        Caller ID says: Fraud Dept
      </text>
      {["8", "0", "4", "2"].map((d, i) => digit(i, d))}
      <rect x="16.5" y="264.5" width="327" height="26" rx="8" fill={C.mint} fillOpacity="0.1" stroke={C.mint} strokeOpacity="0.4" />
      <text x="180" y="282" fontSize="11.5" fontWeight="600" fill={C.mint} textAnchor="middle">
        Hang up. Call the number on your card.
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. glossary: grid of icon chips                                     */
/* ------------------------------------------------------------------ */
type Glyph = "phish" | "squat" | "vish" | "otp" | "urgent" | "verify";

function GlyphShape({ g }: { g: Glyph }): JSX.Element {
  const s = { fill: "none", stroke: C.mint, strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  switch (g) {
    case "phish":
      return (
        <g {...s}>
          <rect x="-9" y="-6.5" width="18" height="13" rx="2.5" />
          <polyline points="-8,-5 0,1.5 8,-5" />
        </g>
      );
    case "squat":
      return (
        <g {...s}>
          <circle cx="0" cy="0" r="8" />
          <ellipse cx="0" cy="0" rx="3.4" ry="8" />
          <line x1="-8" y1="0" x2="8" y2="0" />
        </g>
      );
    case "vish":
      return (
        <g {...s}>
          <rect x="-5" y="-8.5" width="10" height="17" rx="2.5" />
          <line x1="-1.6" y1="5.6" x2="1.6" y2="5.6" />
        </g>
      );
    case "otp":
      return (
        <g {...s}>
          <rect x="-9" y="-5.5" width="18" height="11" rx="2.5" />
          <circle cx="-4.5" cy="0" r="0.6" />
          <circle cx="0" cy="0" r="0.6" />
          <circle cx="4.5" cy="0" r="0.6" />
        </g>
      );
    case "urgent":
      return (
        <g {...s}>
          <circle cx="0" cy="0" r="8" />
          <polyline points="0,-4.5 0,0 3.8,2.4" />
        </g>
      );
    case "verify":
      return (
        <g {...s}>
          <path d="M0 -8.5 L7.5 -5.5 V0.5 C7.5 4.8 3.8 7.4 0 9 C-3.8 7.4 -7.5 4.8 -7.5 0.5 V-5.5 Z" />
          <polyline points="-3.4,0.4 -0.9,3 3.6,-2.4" />
        </g>
      );
  }
}

interface ChipSpec {
  g: Glyph;
  title: string;
  sub: string;
  sub2: string;
}

const CHIPS: ChipSpec[] = [
  { g: "phish", title: "Phishing", sub: "a message built", sub2: "to make you act" },
  { g: "squat", title: "Lookalike domain", sub: "one letter", sub2: "swapped or added" },
  { g: "vish", title: "Vishing", sub: "phishing by", sub2: "phone call" },
  { g: "otp", title: "One-time code", sub: "never read it", sub2: "to a caller" },
  { g: "urgent", title: "Urgency", sub: "a deadline to", sub2: "stop you checking" },
  { g: "verify", title: "Verify yourself", sub: "use a channel", sub2: "you chose" },
];

function TermsArt(): JSX.Element {
  const H = 290;
  return (
    <Frame label="Terms at a glance" aria="Six terms shown as labelled icon chips: phishing, lookalike domain, vishing, one-time code, urgency and verify yourself" height={H}>
      {CHIPS.map((c, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const x = 16 + col * 172;
        const y = 36 + row * 82;
        return (
          <g key={c.g} transform={`translate(${x} ${y})`}>
            <rect x="0.5" y="0.5" width="155" height="74" rx="10" fill={C.panel2} stroke={C.line} />
            <circle
              className="sr-anim-pulse"
              cx="26"
              cy="26"
              r="16"
              fill={C.mint}
              fillOpacity="0.12"
              style={{ transformOrigin: "26px 26px", animationDelay: `${i * 0.35}s` }}
            />
            <g transform="translate(26 26)">
              <GlyphShape g={c.g} />
            </g>
            <text x="51" y="30" fontSize="12.5" fontWeight="700" fill={C.text}>
              {c.title}
            </text>
            <text x="14" y="54" fontSize="10.5" fill={C.muted}>
              {c.sub}
            </text>
            <text x="14" y="67" fontSize="10.5" fill={C.muted}>
              {c.sub2}
            </text>
          </g>
        );
      })}
    </Frame>
  );
}

export const PHISHING_OFFICE_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "One email can lead to an emptied account.",
    illustration: UrgentEmailArt,
    caption: "Read the address. Ignore the deadline.",
    sections: [
      {
        h: "What this is about",
        body: "A normal morning at your desk. An urgent message from your bank lands in your inbox. You follow it to a sign-in page, react to an account warning, and answer a call on your desk phone \u2014 and learn the one check that stops each step.",
      },
      {
        h: "Who it\u2019s for",
        body: "Everyone who reads email, signs in to websites or answers a phone. No technical background needed.",
      },
      {
        h: "Why your judgement matters",
        body: "Filters block a lot, but not every message, and not a phone call. The attacker doesn\u2019t break in \u2014 they ask, and rely on you being rushed. Whether to click, type or answer is a human call. Yours.",
      },
    ],
  },
  {
    tab: "What to watch for",
    headline: "Three steps, each with one tell.",
    illustration: ChainArt,
    caption: "Each step sets up the next. Stop any one.",
    sections: [
      {
        h: "A lookalike address",
        body: "The email and the sign-in page use a domain that is one letter off the real one. HTTPS and a padlock only mean the connection is encrypted, not that the site is genuine.",
      },
      {
        h: "Panic that rushes you",
        body: "\u2018Verify now\u2019, \u2018access suspended\u2019, a balance showing zero. The fear is staged so you skip the checking. A bank doesn\u2019t restore money by asking for your phone number on a web form.",
      },
      {
        h: "A caller who already knows you",
        body: "They know your name and number because you just gave them away. Caller ID can be faked, so \u2018Fraud Department\u2019 on the screen proves nothing.",
      },
      {
        h: "The code is the last lock",
        body: "A one-time code approves the caller\u2019s sign-in, not yours. No real bank asks you to read it out. Hang up and call the number on your card.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: TermsArt,
    caption: "Short definitions \u2014 refer back any time.",
    sections: [
      { h: "Phishing", body: "A message that pretends to be someone you trust to make you click, sign in or hand something over." },
      { h: "Lookalike domain", body: "A web address that differs from the real one by a swapped or extra letter. Also called typosquatting." },
      { h: "Vishing", body: "Voice phishing: a phone call that pretends to be your bank or a colleague to get a code or a payment." },
      { h: "One-time code", body: "A short code from a text or an app that approves one sign-in. Treat it like a password: never read it to anyone." },
      { h: "Urgency", body: "A deadline or a threat added to a message so you act before you think." },
      { h: "Verify out of band", body: "Check a claim through a channel you chose \u2014 the number on your card, the official app \u2014 not the one the message gave you." },
    ],
  },
];
